import React from 'react';

import { useAppDispatch } from '@fd/customHooks/useAppDispatch';
import { Org } from '@flipdish/orgmanagement';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import IconButton from '@mui/material/IconButton';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { useMutation } from '@tanstack/react-query';
import { getTranslate } from 'react-localize-redux';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { notifyError } from '../../../layouts/Notify/actions';
import { clearOrgSwitcherCache, getBrandsForOrg } from '../organisation.services';
import { setCurrentOrg } from '../rms.actions';
import { OrgAndBrandSelectorItems } from './OrgAndBrandSelectorItems';
import SecondaryText from './OrgSelectorItemsSecondaryText';
import { useBrandSwitchPath } from './useBrandSwitchPath';

const StyledSectionHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(1, 2),
}));

const StyledHeaderText = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  fontWeight: 500,
  color: theme.palette.text.secondary,
}));

const StyledIconButton = styled(IconButton)(({ theme }) => ({
  padding: theme.spacing(0.5),
  color: theme.palette.text.secondary,
  '&:hover': {
    color: theme.palette.text.primary,
  },
}));

const StyledDivider = styled('div')(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.divider}`,
  margin: 0,
}));

type Props = {
  orgs: Org[];
  recentOrgs?: Org[];
  open: boolean;
  hasSearched?: boolean;
  searchQuery: string;
  toggleOrgDrawer: () => void;
  setLoadingbrandsForSelectedOrg: (val: boolean) => void;
  setSearchQuery: (val: string) => void;
  onClearRecentOrgs?: () => void;
};

const OrgSelectorItems = ({
  orgs,
  recentOrgs = [],
  open,
  hasSearched,
  searchQuery,
  toggleOrgDrawer,
  setLoadingbrandsForSelectedOrg,
  setSearchQuery,
  onClearRecentOrgs,
}: Readonly<Props>) => {
  const translate = useSelector(getTranslate);
  const history = useHistory();
  const pathForBrand = useBrandSwitchPath();
  const currentOrg = useSelector((state: AppState) => state.rms.currentOrg);
  const dispatch = useAppDispatch();

  const clearCacheMutation = useMutation({
    mutationFn: clearOrgSwitcherCache,
    onSuccess: () => onClearRecentOrgs?.(),
    onError: (error) => {
      dispatch(
        notifyError({
          message: translate('Failed_to_clear_recent_organizations_cache') as string,
          translate: true,
        })
      );
    },
  });

  // Filter recent orgs to exclude current org
  const filteredRecentOrgs = () => {
    if (searchQuery || !recentOrgs.length) {
      return [];
    }

    return recentOrgs.filter((org) => org.orgId !== currentOrg?.orgId);
  };

  const handleOrgSelect = async (org: Org, shouldOpenLinkInNewTab: boolean) => {
    if (!org?.orgId) {
      dispatch(
        notifyError({
          message: translate('No_orgId_found_for_org_name', { name: org?.name }) as string,
          translate: false,
        })
      );
      return;
    }
    if (org.orgId === currentOrg?.orgId) {
      toggleOrgDrawer();
      return;
    }
    try {
      setLoadingbrandsForSelectedOrg(true);
      const brandResponseForOrg = await getBrandsForOrg(org.orgId);
      const brandId = brandResponseForOrg?.data?.data?.[0]?.brandId;
      if (brandId) {
        const path = pathForBrand(brandId);
        if (shouldOpenLinkInNewTab) {
          window.open(path, '_blank');
          return;
        }
        toggleOrgDrawer();
        setSearchQuery('');
        dispatch(setCurrentOrg());
        history.push(path);
      } else {
        dispatch(
          notifyError({
            message: translate('No_brands_found', { orgId: org.orgId }) as string,
            translate: false,
          })
        );
      }
    } catch (e) {
      console.error(e);
      dispatch(notifyError({ message: 'Something_went_wrong' }));
    } finally {
      setLoadingbrandsForSelectedOrg(false);
    }
  };

  return (
    <>
      {/* Recent Orgs Section */}
      {filteredRecentOrgs().length > 0 && (
        <>
          <StyledSectionHeader>
            <StyledHeaderText data-fd="org-brand-selector-recent-orgs-header" variant="body2">
              {translate('Recently_viewed')}
            </StyledHeaderText>
            <StyledIconButton
              size="small"
              onClick={() => clearCacheMutation.mutate()}
              data-fd="org-brand-selector-clear-recent-orgs"
              disabled={clearCacheMutation.isPending}
              aria-label={translate('Clear_recently_viewed_cache') as string}
            >
              <DeleteOutlineIcon fontSize="small" />
            </StyledIconButton>
          </StyledSectionHeader>
          <OrgAndBrandSelectorItems<Org>
            items={filteredRecentOrgs()}
            onItemSelect={handleOrgSelect}
            noResultsText=""
            getItemId={(org) => org.orgId}
            getDisplayProps={(org) => ({
              primary: org.name,
              secondary: <SecondaryText org={org} translate={translate} />,
              imgUrl: org.logoImageUrl,
            })}
            hasSearched={false}
            open={open}
          />
          <StyledDivider />
        </>
      )}

      {/* Regular Orgs Section */}
      <OrgAndBrandSelectorItems<Org>
        items={orgs}
        onItemSelect={handleOrgSelect}
        noResultsText={translate('No_orgs_found_for', { searchTerm: searchQuery }) as string}
        getItemId={(org) => org.orgId}
        getDisplayProps={(org) => ({
          primary: org.name,
          secondary: <SecondaryText org={org} translate={translate} />,
          imgUrl: org.logoImageUrl,
        })}
        hasSearched={hasSearched}
        open={open}
      />
    </>
  );
};

export default OrgSelectorItems;
